// app/javascript/components/Dashboard.jsx
'use client'
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import { Edit2, Trash2, Download, Share2, Video, Info, RefreshCw } from 'lucide-react';
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { DirectUpload } from '@rails/activestorage';
import { Card, CardContent } from "./ui/card";
import { Textarea } from "./ui/textarea";
import { Progress } from "./ui/progress";

// CSRF Token Setup
const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
if (csrfToken) {
    axios.defaults.headers.common['X-CSRF-Token'] = csrfToken;
}

// Helper Functions (unchanged)
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return isNaN(date.getTime())
        ? 'N/A'
        : `${date.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })} ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
};

const statusStyles = {
    queued: 'border-yellow-500 bg-yellow-100 text-yellow-800',
    generating: 'border-blue-500 bg-blue-100 text-blue-800',
    processing: 'border-blue-500 bg-blue-100 text-blue-800',
    success: 'border-green-500 bg-green-100 text-green-800',
    failed: 'border-red-500 bg-red-100 text-red-800',
};

const videoStatusStyles = {
    not_started: 'border-gray-500 bg-gray-100 text-gray-800',
    queued: 'border-yellow-500 bg-yellow-100 text-yellow-800',
    generating: 'border-blue-500 bg-blue-100 text-blue-800',
    processing: 'border-blue-500 bg-blue-100 text-blue-800',
    success: 'border-green-500 bg-green-100 text-green-800',
    failed: 'border-red-500 bg-red-100 text-red-800',
};

const DIRECT_UPLOAD_URL = '/rails/active_storage/direct_uploads';

const Dashboard = ({ initialMedia, pagy, credits }) => {
    const [mediaItems, setMediaItems] = useState(initialMedia || []);
    const [page, setPage] = useState(pagy?.page || 1);
    const [hasMore, setHasMore] = useState(!!pagy?.next);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState({});
    const [file, setFile] = useState(null);
    const [instructions, setInstructions] = useState('');
    const [uploadProgress, setUploadProgress] = useState(0);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editMediaId, setEditMediaId] = useState(null);
    const [editInstructions, setEditInstructions] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteMediaId, setDeleteMediaId] = useState(null);
    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [shareMediaId, setShareMediaId] = useState(null);
    const [videoDialogOpen, setVideoDialogOpen] = useState(false);
    const [videoMediaId, setVideoMediaId] = useState(null);
    const [videoPrompt, setVideoPrompt] = useState('');
    const [tipsDialogOpen, setTipsDialogOpen] = useState(false); // New state for tips dialog

    // Existing fetchMedia, setCredits, updateMedia, and useEffect hooks remain unchanged
    const fetchMedia = useCallback(async (pageNum) => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`/media/dashboard.json?page=${pageNum}`);
            const { media, pagy, credits } = response.data;
            setMediaItems((prev) => (pageNum === 1 ? media || [] : [...prev, ...(media || [])]));
            setHasMore(!!pagy.next);
            setCredits(credits);
        } catch (err) {
            setError('Failed to load media.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    const setCredits = (newCredits) => {
        const desktopElement = document.getElementById("credits-desktop");
        const mobileElement = document.getElementById("credits-mobile");

        if (desktopElement) desktopElement.innerText = newCredits;
        else console.log("credits-desktop not found");

        if (mobileElement) mobileElement.innerText = newCredits;
        else console.log("credits-mobile not found");
    };

    const updateMedia = useCallback(async () => {
        try {
            const response = await axios.get('/media/dashboard.json?page=1');
            const { media, credits } = response.data;
            setMediaItems((prevMedia) => {
                const updatedMap = new Map(media.map(m => [m.id, m]));
                return prevMedia.map(item => updatedMap.get(item.id) || item);
            });
            setCredits(credits);
        } catch (err) {
            console.error('Failed to update media:', err);
        }
    }, []);

    useEffect(() => {
        fetchMedia(page);
    }, [page, fetchMedia]);

    useEffect(() => {
        const interval = setInterval(() => {
            updateMedia();
        }, 120000);
        return () => clearInterval(interval);
    }, [updateMedia]);

    // Existing handleDirectUpload, handleEdit, saveEdit, handleDelete, confirmDelete, downloadImage, handleShare, shareUrl, copyLink, handleGenerateVideo, submitVideoGeneration, loadMore remain unchanged
    const handleDirectUpload = async (e) => {
        e.preventDefault();
        if (!file) return;

        setActionLoading((prev) => ({ ...prev, upload: true }));
        setUploadProgress(0);

        try {
            const upload = new DirectUpload(file, DIRECT_UPLOAD_URL, {
                directUploadWillStoreFileWithXHR: (xhr) => {
                    xhr.upload.addEventListener('progress', (event) => {
                        if (event.lengthComputable) {
                            const percent = Math.round((event.loaded / event.total) * 100);
                            setUploadProgress(percent);
                        }
                    });
                }
            });

            const { signed_id } = await new Promise((resolve, reject) => {
                upload.create((error, blob) => {
                    if (error) reject(error);
                    else resolve(blob);
                });
            });

            const mediaResponse = await axios.post('/media.json', {
                media: { original_image: signed_id, instructions }
            });

            setMediaItems((prev) => [mediaResponse.data, ...prev]);
            setFile(null);
            setInstructions('');
            setUploadProgress(0);
        } catch (err) {
            const errorMsg = err.message?.includes('net::ERR_NAME_NOT_RESOLVED')
                ? 'Upload failed due to a network issue. Please check your connection.'
                : err.response?.data?.errors.join(' ') || 'Failed to upload media.';
            showJetToast(errorMsg);
            console.error(err);
        } finally {
            setActionLoading((prev) => ({ ...prev, upload: false }));
        }
    };

    const handleEdit = (media) => {
        setEditMediaId(media.id);
        setEditInstructions(media.instructions || '');
        setEditDialogOpen(true);
    };

    const saveEdit = async () => {
        setActionLoading((prev) => ({ ...prev, [`edit_${editMediaId}`]: true }));
        try {
            const response = await axios.put(`/media/${editMediaId}`, {
                media: { instructions: editInstructions }
            });
            setMediaItems((prev) =>
                prev.map((item) =>
                    item.id === editMediaId ? { ...item, instructions: editInstructions } : item
                )
            );
            setEditDialogOpen(false);
        } catch (err) {
            let error = err.response?.data?.error || 'Failed to update media.';
            setError(error);
            showJetToast(error);
            console.error(err);
        } finally {
            setActionLoading((prev) => ({ ...prev, [`edit_${editMediaId}`]: false }));
        }
    };

    const handleDelete = (mediaId) => {
        const media = mediaItems.find(m => m.id === mediaId);
        if (media.status !== 'queued') {
            setError('Can only delete media in queued status.');
            return;
        }
        setDeleteMediaId(mediaId);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = async () => {
        setActionLoading((prev) => ({ ...prev, [`delete_${deleteMediaId}`]: true }));
        try {
            await axios.delete(`/media/${deleteMediaId}.json`);
            setMediaItems((prev) => prev.filter((m) => m.id !== deleteMediaId));
            setDeleteDialogOpen(false);
        } catch (err) {
            setError('Failed to delete media.');
            console.error(err);
        } finally {
            setActionLoading((prev) => ({ ...prev, [`delete_${deleteMediaId}`]: false }));
        }
    };

    const downloadImage = (url) => {
        window.open(url, '_blank');
    };

    const handleShare = (mediaId) => {
        setShareMediaId(mediaId);
        setShareDialogOpen(true);
    };

    const shareUrl = () => {
        const media = mediaItems.find(m => m.id === shareMediaId);
        return media ? `${window.location.origin}/${media.user_id}/share` : '';
    };

    const copyLink = () => {
        navigator.clipboard.writeText(shareUrl());
        showJetToast('Link copied to clipboard!');
    };

    const handleGenerateVideo = (mediaId) => {
        setVideoMediaId(mediaId);
        setVideoPrompt('');
        setVideoDialogOpen(true);
    };

    const submitVideoGeneration = async () => {
        setActionLoading((prev) => ({ ...prev, [`video_${videoMediaId}`]: true }));
        try {
            const response = await axios.post(`/media/${videoMediaId}/generate_video`, {
                video_prompt: videoPrompt
            });
            setMediaItems((prev) =>
                prev.map((item) =>
                    item.id === videoMediaId ? response.data : item
                )
            );
            setVideoDialogOpen(false);
        } catch (err) {
            showJetToast(err.response?.data?.errors?.join(', ') || 'Failed to generate video.');
            console.error(err);
        } finally {
            setActionLoading((prev) => ({ ...prev, [`video_${videoMediaId}`]: false }));
        }
    };

    const loadMore = () => hasMore && !loading && setPage((prev) => prev + 1);

    const handleRetryImage = async (mediaId) => {
        setActionLoading((prev) => ({ ...prev, [`retry_${mediaId}`]: true }));
        try {
            const response = await axios.post(`/media/${mediaId}/retry_image`);
            setMediaItems((prev) =>
                prev.map((item) =>
                    item.id === mediaId ? { ...response.data, id: mediaId } : item
                )
            );
            showJetToast('Retry initiated successfully!');
        } catch (err) {
            showJetToast(err.response?.data?.errors?.join(', ') || 'Failed to retry image generation.');
            console.error(err);
        } finally {
            setActionLoading((prev) => ({ ...prev, [`retry_${mediaId}`]: false }));
        }
    };

    return (
        <div className="min-h-screen bg-pink-50 p-4 lg:p-8 font-nunito">
            <h1 className="text-3xl lg:text-4xl font-black mb-6 px-6 py-2">
                Your Media Dashboard
            </h1>

            <Card className="mb-8 border-4 border-black bg-pink-100 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
                <CardContent className="p-6">
                    <form onSubmit={handleDirectUpload} className="space-y-4">
                        <div className="flex flex-row items-center gap-2">
                            <label className="block text-xl font-bold">Upload Image</label>
                            <Info className="w-4 h-4 cursor-pointer hover:text-pink-300" onClick={() => setTipsDialogOpen(true)} />
                        </div>
                        <div>
                            <Input
                                type="file"
                                onChange={(e) => setFile(e.target.files[0])}
                                accept="image/*"
                                className="border-2 border-black rounded-xl"
                            />
                        </div>
                        <div>
                            <label className="block text-xl font-bold mb-2">Instructions (Optional)</label>
                            <Textarea
                                value={instructions}
                                onChange={(e) => setInstructions(e.target.value)}
                                className="border-2 border-black rounded-xl"
                                rows={3}
                            />
                        </div>
                        {actionLoading.upload && (
                            <Progress value={uploadProgress} className="w-full border-2 border-black" />
                        )}
                        <Button
                            type="submit"
                            disabled={!file || actionLoading.upload || !credits || credits <= 0}
                            className="bg-pink-500 text-white font-bold border-2 border-black rounded-xl hover:bg-pink-600"
                        >
                            {actionLoading.upload ? 'Uploading...' : 'Ghiblify Image'}
                        </Button>
                        {!credits || credits <= 0 ? (
                            <div className="my-4 px-4">
                                Add credits to start converting images to viral style
                                <div>refresh if you purchased and can't see your credits.</div>
                            </div>
                        ) : null}
                    </form>
                </CardContent>
            </Card>

            {/* Tips Dialog */}
            <Dialog open={tipsDialogOpen} onOpenChange={setTipsDialogOpen}>
                <DialogContent className="bg-white border-2 border-black rounded-xl">
                    <DialogHeader>
                        <DialogTitle>Tips for Better Photos</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-gray-600">Follow these quick tips to get the best results!</p>
                        <ul className="text-md space-y-2 text-gray-600">
                            <li className="flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-check h-4 w-4 shrink-0 text-green-600"><path d="M20 6 9 17l-5-5"></path></svg>
                                Small group of people (Best 1-4 people)
                            </li>
                            <li className="flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-check h-4 w-4 shrink-0 text-green-600"><path d="M20 6 9 17l-5-5"></path></svg>
                                High resolution photos
                            </li>
                            <li className="flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-check h-4 w-4 shrink-0 text-green-600"><path d="M20 6 9 17l-5-5"></path></svg>
                                Good lighting
                            </li>
                            <li className="flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-check h-4 w-4 shrink-0 text-green-600"><path d="M20 6 9 17l-5-5"></path></svg>
                                Natural expressions
                            </li>
                            <li className="flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-check h-4 w-4 shrink-0 text-green-600"><path d="M20 6 9 17l-5-5"></path></svg>
                                Simple background
                            </li>
                        </ul>
                        <ul className="text-md space-y-2 text-gray-600">
                            <li className="flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x h-4 w-4 shrink-0 text-red-600"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
                                Big group of people (Above 6 people)
                            </li>
                            <li className="flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x h-4 w-4 shrink-0 text-red-600"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
                                Blur/low resolution photos
                            </li>
                            <li className="flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x h-4 w-4 shrink-0 text-red-600"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
                                Poor lighting
                            </li>
                            <li className="flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x h-4 w-4 shrink-0 text-red-600"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
                                Too much movement in image
                            </li>
                            <li className="flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x h-4 w-4 shrink-0 text-red-600"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
                                Complicated background with many items
                            </li>
                        </ul>
                    </div>
                    <DialogFooter>
                        <Button
                            onClick={() => setTipsDialogOpen(false)}
                            className="bg-pink-500 text-white border-2 border-black rounded-xl hover:bg-pink-600"
                        >
                            Got it
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Rest of your existing JSX remains unchanged */}
            {mediaItems && mediaItems.length > 0 ? (
                <div className="p-4 bg-white my-4 text-sm px-8">
                    It can take from 30 minutes to 2 hours for the images to generate. We are trying our best due to heavy user load.
                    Once generated you will see a download button on the image card.
                    <div className="my-2">
                        Video generation takes about 3-6 minutes. For any support contact us at <span className="font-bold">hello@giblifyai.com</span>
                    </div>
                </div>
            ) : null}

            {error && (
                <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-xl border-2 border-red-500">{error}</div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {loading && mediaItems.length === 0 ? (
                    Array.from({ length: 4 }).map((_, index) => (
                        <Skeleton key={index} className="w-full h-[400px] rounded-xl border-4 border-black" />
                    ))
                ) : mediaItems.length === 0 ? (
                    <div className="col-span-full text-center py-8">
                        <p className="text-xl text-gray-600">No media found. Start uploading some!</p>
                    </div>
                ) : (
                    mediaItems.map((media) => {
                        const statusStyle = statusStyles[media.status] || statusStyles.queued;
                        const videoStatusStyle = videoStatusStyles[media.video_status] || videoStatusStyles.not_started;
                        const isSuccess = media.status === 'success';
                        const isVideoSuccess = media.video_status === 'success';

                        return (
                            <Card
                                key={media.id}
                                className={`rounded-xl border-4 ${statusStyle.split(' ')[0]} bg-white hover:shadow-lg transition-shadow overflow-hidden flex flex-col`}
                            >
                                <div className="relative bg-gradient-to-br from-pink-200 to-blue-200">
                                    <div className="absolute top-2 left-2 flex flex-col gap-2">
                                        <span className={`px-2 py-1 rounded border ${statusStyle} font-bold text-xs`}>
                                            {media.status?.charAt(0)?.toUpperCase() + media.status?.slice(1)}
                                        </span>
                                        {media.video_status !== 'not_started' && (
                                            <span className={`px-2 py-1 rounded border ${videoStatusStyle} font-bold text-xs`}>
                                                Video: {media.video_status?.charAt(0)?.toUpperCase() + media.video_status?.slice(1)?.replace('_', ' ')}
                                            </span>
                                        )}
                                    </div>
                                    <div className="absolute top-2 right-2 flex gap-2">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            disabled={media.status !== 'queued'}
                                            className="bg-white border-2 border-black hover:bg-pink-100"
                                            onClick={(e) => { e.stopPropagation(); handleEdit(media); }}
                                        >
                                            <Edit2 className="w-4 h-4" />
                                        </Button>
                                        {isSuccess && (
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="bg-white border-2 border-black hover:bg-pink-100"
                                                onClick={(e) => { e.stopPropagation(); handleShare(media.id); }}
                                            >
                                                <Share2 className="w-4 h-4" />
                                            </Button>
                                        )}
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="bg-white border-2 border-black hover:bg-red-100"
                                            onClick={(e) => { e.stopPropagation(); handleDelete(media.id); }}
                                            disabled={actionLoading[`delete_${media.id}`] || media.status !== 'queued'}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                    <div className="flex flex-col justify-center items-center mt-2 space-y-2 p-2">
                                        {media.original_image_url && (
                                            <img
                                                src={media.original_image_url}
                                                alt="Original"
                                                className="w-full h-48 object-contain"
                                            />
                                        )}
                                        {media.generated_image_url ? (
                                            <img
                                                src={media.generated_image_url}
                                                alt="Generated"
                                                className="w-full h-48 object-contain"
                                            />
                                        ) : (
                                            <Skeleton className="w-full h-48 rounded" />
                                        )}
                                    </div>
                                </div>
                                <CardContent className="p-4 flex-grow">
                                    <div className="mt-2 space-y-2">
                                        {isSuccess && (
                                            <Button
                                                onClick={() => handleGenerateVideo(media.id)}
                                                className="w-full bg-pink-500 text-white font-bold border-2 border-black rounded-xl hover:bg-pink-600"
                                                disabled={media.video_status === 'queued' || media.video_status === 'generating'}
                                            >
                                                <Video className="w-4 h-4 mr-2" />
                                                {media.video_status === 'queued' || media.video_status === 'generating' ? 'Video Generating...' : 'Generate Video (3 Credits)'}
                                            </Button>
                                        )}
                                        <div className="flex gap-2">
                                            <Button
                                                onClick={() => downloadImage(media.generated_image_url)}
                                                className="w-1/2 bg-green-500 text-white font-bold border-2 border-black rounded-xl hover:bg-green-600"
                                                disabled={!isSuccess || !media.generated_image_url}
                                            >
                                                <Download className="w-4 h-4 mr-2" />
                                                Image
                                            </Button>
                                            <Button
                                                onClick={() => downloadImage(media.generated_video_url)}
                                                className="w-1/2 bg-green-500 text-white font-bold border-2 border-black rounded-xl hover:bg-green-600"
                                                disabled={!isVideoSuccess || !media.generated_video_url}
                                            >
                                                <Download className="w-4 h-4 mr-2" />
                                                Video
                                            </Button>
                                        </div>
                                        <div className="text-xs text-gray-600">
                                            <p>Created: {formatDate(media.created_at)}</p>
                                            <p>Updated: {formatDate(media.updated_at)}</p>
                                            {media.failure_reason && (
                                                <p className="text-red-600">Image Error: {media.failure_reason}</p>
                                            )}
                                            {media.video_failure_reason && (
                                                <p className="text-red-600">Video Error: {media.video_failure_reason}</p>
                                            )}
                                        </div>
                                        {media.status === 'failed' && (
                                            <div className="mt-2">
                                                <p className="text-xs text-green-600 mb-1">Credit refunded for failed generation</p>
                                                <Button
                                                    onClick={() => handleRetryImage(media.id)}
                                                    className="w-full bg-orange-500 !text-white font-bold border-2 border-black rounded-xl hover:bg-orange-600"
                                                    disabled={actionLoading[`retry_${media.id}`]}
                                                >
                                                    <RefreshCw className={`w-4 h-4 mr-2 text-white ${actionLoading[`retry_${media.id}`] ? 'animate-spin' : ''}`} />
                                                    <span className="text-white">{actionLoading[`retry_${media.id}`] ? 'Retrying...' : 'Retry (1 Credit)'}</span>
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })
                )}
            </div>

            {mediaItems.length > 0 && (
                <div className="mt-8 flex justify-center">
                    {loading ? (
                        <div className="flex items-center gap-2">
                            <Skeleton className="w-6 h-6 rounded-full animate-spin" />
                            <span className="text-gray-600">Loading...</span>
                        </div>
                    ) : hasMore ? (
                        <Button onClick={loadMore} className="px-6 py-2 bg-pink-500 text-white font-bold rounded-xl border-2 border-black hover:bg-pink-600">
                            Load More
                        </Button>
                    ) : (
                        <p className="text-gray-600 text-lg">No more media to load!</p>
                    )}
                </div>
            )}

            {/* Edit Dialog */}
            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                <DialogContent className="bg-white border-2 border-black rounded-xl">
                    <DialogHeader>
                        <DialogTitle>Edit Media Instructions</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="instructions">Instructions</Label>
                            <Textarea
                                id="instructions"
                                value={editInstructions}
                                onChange={(e) => setEditInstructions(e.target.value)}
                                className="border-2 border-black rounded-xl"
                                rows={3}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setEditDialogOpen(false)}
                            className="border-2 border-black rounded-xl"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={saveEdit}
                            className="bg-pink-500 text-white border-2 border-black rounded-xl hover:bg-pink-600"
                            disabled={actionLoading[`edit_${editMediaId}`]}
                        >
                            {actionLoading[`edit_${editMediaId}`] ? 'Saving...' : 'Save'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent className="bg-white border-2 border-black rounded-xl">
                    <DialogHeader>
                        <DialogTitle>Delete Media</DialogTitle>
                    </DialogHeader>
                    <p>Are you sure you want to delete this media? This action cannot be undone.</p>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDeleteDialogOpen(false)}
                            className="border-2 border-black rounded-xl"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={confirmDelete}
                            className="bg-red-500 text-white border-2 border-black rounded-xl hover:bg-red-600"
                            disabled={actionLoading[`delete_${deleteMediaId}`]}
                        >
                            {actionLoading[`delete_${deleteMediaId}`] ? 'Deleting...' : 'Delete'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Share Dialog */}
            <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
                <DialogContent className="bg-white border-2 border-black rounded-xl">
                    <DialogHeader>
                        <DialogTitle>Share Generated Image</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="flex flex-wrap gap-4 justify-center">
                            <a
                                href={`https://wa.me/?text=${encodeURIComponent(`Check out my Ghibli style image: ${shareUrl()}`)}`}
                                target="_blank"
                                className="bg-green-500 px-4 py-2 text-white border-2 border-black rounded-xl hover:bg-green-600"
                            >
                                WhatsApp
                            </a>
                            <a
                                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check out my Ghibli style image: ${shareUrl()}`)}`}
                                target="_blank"
                                className="bg-blue-500 px-4 py-2 text-white border-2 border-black rounded-xl hover:bg-blue-600"
                            >
                                X
                            </a>
                            <a
                                href={`https://t.me/share/url?url=${encodeURIComponent(shareUrl())}&text=${encodeURIComponent('Check out my Ghibli style image')}`}
                                target="_blank"
                                className="bg-blue-400 px-4 py-2 text-white border-2 border-black rounded-xl hover:bg-blue-500"
                            >
                                Telegram
                            </a>
                            <a
                                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl())}`}
                                target="_blank"
                                className="bg-blue-600 px-4 py-2 text-white border-2 border-black rounded-xl hover:bg-blue-700"
                            >
                                Facebook
                            </a>
                            <Button
                                onClick={copyLink}
                                className="bg-gray-500 text-white border-2 border-black rounded-xl hover:bg-gray-600"
                            >
                                Copy Link
                            </Button>
                        </div>
                        {shareMediaId && (
                            <img
                                src={mediaItems.find(m => m.id === shareMediaId)?.generated_image_url}
                                alt="Generated"
                                className="w-full h-64 object-contain mt-4"
                            />
                        )}
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setShareDialogOpen(false)}
                            className="border-2 border-black rounded-xl"
                        >
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Video Generation Dialog */}
            <Dialog open={videoDialogOpen} onOpenChange={setVideoDialogOpen}>
                <DialogContent className="bg-white border-2 border-black rounded-xl">
                    <DialogHeader>
                        <DialogTitle>Generate Video</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="video_prompt">Video Prompt</Label>
                            <Textarea
                                id="video_prompt"
                                value={videoPrompt}
                                onChange={(e) => setVideoPrompt(e.target.value)}
                                placeholder="Imagine the image is the first scene of your video &#10;then describe what should happen in the video..., e.g. 'Looking at each other then hugging', 'smiling and dancing', ' etc."
                                className="border-2 mt-4 border-black rounded-xl"
                                rows={3}
                            />
                        </div>
                        {videoMediaId && (
                            <img
                                src={mediaItems.find(m => m.id === videoMediaId)?.generated_image_url}
                                alt="Generated Image"
                                className="w-full h-64 object-contain mt-4"
                            />
                        )}
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setVideoDialogOpen(false)}
                            className="border-2 border-black rounded-xl"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={submitVideoGeneration}
                            className="bg-pink-500 text-white border-2 border-black rounded-xl hover:bg-pink-600"
                            disabled={actionLoading[`video_${videoMediaId}`] || !videoPrompt}
                        >
                            {actionLoading[`video_${videoMediaId}`] ? 'Generating...' : 'Generate Video (3 Credits)'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

Dashboard.propTypes = {
    initialMedia: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        original_image_url: PropTypes.string,
        generated_image_url: PropTypes.string,
        generated_video_url: PropTypes.string,
        status: PropTypes.string,
        video_status: PropTypes.string,
        instructions: PropTypes.string,
        video_prompt: PropTypes.string,
        failure_reason: PropTypes.string,
        video_failure_reason: PropTypes.string,
        created_at: PropTypes.string,
        updated_at: PropTypes.string,
        user_id: PropTypes.string
    })),
    pagy: PropTypes.shape({
        page: PropTypes.number,
        next: PropTypes.number
    }),
    credits: PropTypes.number
};

export default Dashboard;